import type { Transaction } from "@rocicorp/zero";
import * as z from "zod";
import { userFacingError } from "../authorization";
import { getStepContentHash, readStepConfig } from "../commitFiles";
import { retentionSecondsFromRunExpiry } from "../executionRetention";
import { zql } from "../generatedDrizzleSchema";
import { mutator } from "./mutator";
import { generateId } from "../id";
import { clientGeneratableIdSchema } from "../id";

export type ReEmitExecutionSource = {
  id: string;
  runId: string | null;
  workflowId: string | null;
  branchId: string | null;
  stepDir: string | null;
  status: string;
  outputContentType: string | null;
  contentHash: string | null;
};

export type ReEmitExecutionImplArgs = {
  newExecutionId: string;
  execution: ReEmitExecutionSource;
};

export async function reEmitExecutionImpl({
  tx,
  args: { execution },
}: {
  tx: Transaction;
  args: ReEmitExecutionImplArgs;
}): Promise<{
  downstreamIds: string[];
  downstreamSteps: Array<{
    executionId: string;
    stepDir: string;
    contentHash: string;
  }>;
  commitId: string;
  createdAt: number;
  retentionSeconds: number;
}> {
  if (execution.status !== "success")
    throw userFacingError("Execution not successful");
  if (!execution.stepDir)
    throw userFacingError("Cannot re-emit a non-step execution");
  if (!execution.runId) throw userFacingError("Execution has no run");

  const stepDir = execution.stepDir;

  // Use the branch's *current* head, not `run.commitId`, so re-emit triggers
  // the current downstream graph.
  const [run] = await tx.run(
    zql.workflowRuns.where("id", execution.runId).limit(1)
  );
  if (!run) throw userFacingError("Run not found");

  const branch = await tx.run(
    zql.workflowBranches.where("id", run.branchId).one()
  );
  if (!branch?.headCommitId) throw userFacingError("Branch not found");
  const commitId = branch.headCommitId;

  const now = Date.now();

  const config = await readStepConfig({ tx, commitId }, stepDir);
  const targets = config.links ?? [];
  const downstreamIds: string[] = [];
  const downstreamSteps: Array<{
    executionId: string;
    stepDir: string;
    contentHash: string;
  }> = [];

  for (const targetDir of targets) {
    const targetConfig = await readStepConfig({ tx, commitId }, targetDir);
    if (targetConfig.route) continue;

    const downstreamId = generateId();
    downstreamIds.push(downstreamId);
    const downstreamHash = await getStepContentHash(tx, commitId, targetDir);
    downstreamSteps.push({
      executionId: downstreamId,
      stepDir: targetDir,
      contentHash: downstreamHash,
    });
  }

  return {
    downstreamIds,
    downstreamSteps,
    commitId,
    createdAt: now,
    retentionSeconds: retentionSecondsFromRunExpiry(
      run.createdAt,
      run.expiresAt
    ),
  };
}

export const reEmitRetainedExecutionSchema = z.object({
  executionId: z.string(),
  newExecutionId: clientGeneratableIdSchema,
  workflowId: z.string(),
});

export const reEmitRetainedExecution = mutator({
  schema: reEmitRetainedExecutionSchema,
  resolve: {
    workflow: { kind: "workflow", from: "workflowId" },
  },
  permissions: [
    {
      permission: "space:write",
      scopeId: ({ args }) => args.workflow.spaceId,
    },
  ],
  fn: async () => {},
});
